import React from 'react'
import { graphql } from 'gatsby'
import { BREAKPOINT, COLOR } from '@farewill/ui/tokens'
import { Divider, Wrapper } from '@farewill/ui'

import PATHS from 'paths'
import { TRUSTPILOT_RATING_OUT_OF_5, TRUSTPILOT_REVIEWS_TOTAL } from 'config'
import { PRODUCTS } from 'lib/products/constants'
import { TRACKING_TYPES } from 'lib/tracking/constants'
import { roundDownToNearestHundred } from 'lib/formatting/numbers'
import DefaultLayout from 'layouts/DefaultLayout'
import BreakpointSwitch from 'components/BreakpointSwitch'
import CoBrandedHeader from 'components/Header/Custom/CoBranded'
import PreventOrphan from 'components/PreventOrphan'
import GetStarted from 'views/wills/components/Overview/GetStarted'

import Hero from '../slices/Hero'
import Trustpilot from '../slices/Trustpilot'
import Pledges from '../slices/Pledges'
import FAQ from '../slices/FAQ'
import WillOptions from '../slices/WillOptions'
import GiftStories from '../slices/GiftStories'
import GiftSlider from '../slices/GiftSlider'

import type { GreyLabelLandingPageProps } from '../types'

const GreyLabelLandingPage = ({
  data: {
    page: {
      utmCampaign,
      utmMedium,
      partner,
      heroTitle,
      heroSubtitle,
      heroCtaText,
    },
    faqSection,
  },
}: GreyLabelLandingPageProps): React.ReactElement => (
  <DefaultLayout
    branding={partner.utmSource}
    title="Online Will Writing Service | Legal Will In 15 Minutes"
    description={`Our solicitor-approved guide helps you write a will online from the comfort of your own home. Rated ${TRUSTPILOT_RATING_OUT_OF_5} out of 5 by over ${roundDownToNearestHundred(
      TRUSTPILOT_REVIEWS_TOTAL
    )} customers on Trustpilot. National Will Writing Firm of the Year.`}
    meta={[
      {
        name: 'robots',
        content: 'noindex',
      },
    ]}
    customHeader={
      <CoBrandedHeader
        altText="Marie Curie"
        assetDirectory="marie-curie"
        partnerLogoUrl="https://www.mariecurie.org.uk/"
      />
    }
    product={PRODUCTS.WILLS}
    canonicalPath={PATHS.WILLS.OVERVIEW}
    headerColor={COLOR.WHITE}
    footerType="compact"
    utmParameters={{
      partnerId: partner?.partnerId || TRACKING_TYPES.NOT_SET,
      utmCampaign: utmCampaign || TRACKING_TYPES.NOT_SET,
      utmMedium: utmMedium || TRACKING_TYPES.NOT_SET,
      utmSource: partner?.utmSource || TRACKING_TYPES.NOT_SET,
      utmChannel: partner?.utmChannel || TRACKING_TYPES.NOT_SET,
    }}
  >
    <Wrapper background={COLOR.WHITE}>
      <Hero
        assetDirectory="marie-curie"
        title={heroTitle || 'Write your will for free today'}
        subtitle={heroSubtitle?.heroSubtitle}
        ctaText={heroCtaText || 'Let’s get started'}
        minHeight={650}
        imageTransformationParams={{
          small: 'ar_1.8,c_fill,f_auto,q_auto,g_faces,w_300',
          medium: 'ar_2.23,c_fill,f_auto,q_auto,g_faces,w_964',
          large: 'ar_1.05,c_fill,f_auto,q_auto,g_faces,h_650',
          extraLarge: 'ar_1.4,c_crop,f_auto,q_auto,g_faces,z_0.9/h_650',
        }}
        telephoneWillsEnabled={false}
      />

      <WillOptions partnerName="Marie Curie" telephoneWillsEnabled={false} />

      <Wrapper padding={['XL', 0, 'XXL']} paddingFromL={[96, 0, 'XXL']}>
        <GiftStories
          stories={[
            {
              title: 'Join supporters like Lynn to make a lasting difference',
              description:
                'I just want every family that is going through that awful time to have that support there that we had, because it just makes such a difference.',
              source: 'Lynn, whose dad was cared for by Marie Curie',
              image:
                'com-assets/greylabel-landing-page-assets/marie-curie/case-study-1-6-2026',
              imageTransformationParams: {
                large:
                  'ar_1,w_842,c_crop,g_xy_center,x_500,y_421,z_1/c_fit,w_400,r_max',
              },
            },
            {
              title: 'Help people like Rachel give care when it’s needed most',
              description:
                'As a Healthcare Assistant, I can see how important the gift in my Will is. After my loved ones are looked after, the remainder of my estate will go to Marie Curie, as I know how much our patients need it.',
              source: 'Marie Curie Healthcare Assistant, Rachel',
              image:
                'com-assets/greylabel-landing-page-assets/marie-curie/case-study-2',
              imageTransformationParams: {
                small:
                  'ar_1.8,w_3400,c_crop,g_xy_center,x_2500,y_1280,f_auto,q_auto/w_648',
                medium:
                  'ar_2.5,w_3950,c_crop,g_xy_center,x_3800,y_1300,z_0.1/c_fit,w_1264,w_964',
                large:
                  'ar_1,w_2000,c_crop,g_xy_center,x_2500,y_1280,z_1/c_fit,w_400,r_max',
              },
            },
          ]}
          title={
            <>
              A quarter of people cared for by{' '}
              <PreventOrphan>Marie Curie</PreventOrphan> is thanks to gifts in
              Wills
            </>
          }
        />
      </Wrapper>

      <GiftSlider
        caption="The calculations use an average estate value of £592,000 (Smee & Ford, 2023), audited Marie Curie figures, and average costs of services across the UK."
        partner="Marie Curie"
        config={[
          {
            value: 0,
            label: '5%',
            text: '5% of an average estate could help provide 142 people with nursing care overnight, so that Marie Curie’s nursing staff can manage pain and provide comfort for patients and loved ones.',
            image:
              'ar_1,c_fill,f_auto,q_auto/com-assets/greylabel-landing-page-assets/marie-curie/gift-5',
          },
          {
            value: 25,
            label: '10%',
            text: '10% of an average estate could help fund 1,286 hours of nursing care, in a hospice or at home, helping people to be cared for in the place they choose.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/marie-curie/gift-10',
          },
          {
            value: 50,
            label: '25%',
            text: '25% of an average estate could help fund over 2 weeks of specialist 24-hour care in a Marie Curie hospice, ensuring patients and their loved ones feel safe and comfortable.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/marie-curie/gift-25',
          },
          {
            value: 75,
            label: '50%',
            text: '50% of an average estate could help fund 7 nursing posts for a year, helping Marie Curie to reach more people living with a terminal illness.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/marie-curie/gift-50',
          },
          {
            value: 100,
            label: '100%',
            text: '100% of an average estate could help fully fund all of the medical care, emotional support and wellbeing services in a hospice for 65 days.',
            image:
              'ar_1,c_fill,f_auto,q_auto,w_300/com-assets/greylabel-landing-page-assets/marie-curie/gift-100',
          },
        ]}
      />

      <Wrapper container noTrim>
        <Trustpilot
          tag="marie-curie"
          title="Farewill is trusted by Marie Curie supporters"
        />
        <BreakpointSwitch breakAt={BREAKPOINT.M} aboveComponent={<Divider />} />
        <FAQ
          questions={faqSection.questions}
          imagePath="com-assets/greylabel-landing-page-assets/marie-curie/faq"
        />
        <BreakpointSwitch breakAt={BREAKPOINT.M} aboveComponent={<Divider />} />
        <Pledges
          title="Over £20 million has been gifted since 2020"
          description="Whether your gift is large or small, you’ll be helping Marie Curie to fund vital services for people who need them."
          imagePath="fl_sanitize/cobranding-partner-logos/marie-curie/marie-curie-logo"
        />
      </Wrapper>

      <GetStarted
        partnerCampaignReferral
        telephoneWillsEnabled={false}
        title="Ready to write your will?"
      />
    </Wrapper>
  </DefaultLayout>
)

export const query = graphql`
  query MarieCurieGreyLabelPartnerLandingPage($slug: String!) {
    page: contentfulPartnerCampaignPage(slug: { eq: $slug }) {
      utmCampaign
      utmMedium
      heroTitle
      heroSubtitle {
        heroSubtitle
      }
      heroCtaText
      partner {
        partnerId
        telephoneWillsEnabled
        utmSource
        utmChannel
      }
      telephoneWillsEnabled
    }
    faqSection: contentfulFaqSection(
      contentful_id: { eq: "6uMQ457fUSavj0VQF1ASGG" }
    ) {
      ...FaqSectionFields
    }
  }
`

export default GreyLabelLandingPage
